import React from 'react';
import { useData } from 'vike-react/useData';
import Footer from '../../../components/Footer';
import { Link } from '../../../components/Link';
import type { Data } from './+data.server';

const shell = {
  page: 'min-h-screen bg-[#F8F5F0] dark:bg-[#170F07]',
  inner: 'max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12',
  card: 'rounded-2xl border border-[#DDD5C8] bg-white shadow-sm dark:border-[#4A3520] dark:bg-[#1F1509] overflow-hidden',
  h1: 'text-2xl sm:text-3xl font-bold text-[#1E1A14] dark:text-[#F2EADF]',
  h2: 'text-lg font-semibold text-[#1E1A14] dark:text-[#F2EADF]',
  body: 'text-sm sm:text-base text-[#3D3529] dark:text-[#C4A882] leading-relaxed',
  muted: 'text-sm text-[#7A6E5F] dark:text-[#B8A48C]',
  link: 'text-[#3B5E45] hover:text-[#2A4533] dark:text-[#98CFA8] dark:hover:text-[#B6D4BC] font-medium underline-offset-2 hover:underline',
  pill: 'inline-flex items-center rounded-full border border-[#DDD5C8] bg-[#F2EADF] px-3 py-1 text-xs font-medium text-[#3D3529] dark:border-[#4A3520] dark:bg-[#2A1D0F] dark:text-[#C4A882]',
} as const;

export default function AuthorProfilePage(): React.JSX.Element {
  const { profile } = useData<Data>();

  return (
    <div className={shell.page}>
      <div className={shell.inner}>
        <nav className="mb-6 text-sm text-[#7A6E5F] dark:text-[#B8A48C]" aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link href="/" className={shell.link}>
                Home
              </Link>
            </li>
            <li aria-hidden className="select-none">
              /
            </li>
            <li>
              <Link href="/en/resources" className={shell.link}>
                Resources
              </Link>
            </li>
            <li aria-hidden className="select-none">
              /
            </li>
            <li className="text-[#1E1A14] dark:text-[#F2EADF]" aria-current="page">
              {profile.displayName}
            </li>
          </ol>
        </nav>

        <article className={shell.card}>
          <div className="flex flex-col sm:flex-row gap-6 p-6 sm:p-8 border-b border-[#DDD5C8] dark:border-[#4A3520]">
            <div className="shrink-0 flex justify-center sm:justify-start">
              <img
                src={profile.headshotPath}
                alt={`${profile.displayName} — author photo`}
                width={160}
                height={160}
                className="h-40 w-40 rounded-2xl object-cover border border-[#DDD5C8] dark:border-[#4A3520] shadow-sm"
                loading="eager"
              />
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <h1 className={`${shell.h1} mb-1`}>{profile.displayName}</h1>
              <p className={`${shell.muted} mb-4`}>{profile.jobTitle}</p>
              <p className={`${shell.body} mb-6`}>{profile.bio}</p>
              {profile.socialLinks.length > 0 ? (
                <ul className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-x-4 sm:gap-y-2 justify-center sm:justify-start">
                  {profile.socialLinks.map((link) => (
                    <li key={`${link.label}-${link.url}`}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${shell.link} inline-flex items-center gap-1.5`}
                      >
                        {link.label}
                        <span aria-hidden className="text-xs">
                          ↗
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-8">
            <section aria-labelledby="coverage-heading">
              <h2 id="coverage-heading" className={`${shell.h2} mb-3`}>
                Areas of coverage
              </h2>
              <ul className="flex flex-wrap gap-2">
                {profile.coverageAreas.map((area) => (
                  <li key={area}>
                    <span className={shell.pill}>{area}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section aria-labelledby="articles-heading">
              <h2 id="articles-heading" className={`${shell.h2} mb-3`}>
                Articles on JobCrawls
              </h2>
              {profile.articles.length === 0 ? (
                <p className={shell.muted}>No linked guides yet.</p>
              ) : (
                <ul className="space-y-2">
                  {profile.articles.map((article) => (
                    <li key={article.href}>
                      <Link href={article.href} className={shell.link}>
                        {article.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
}
